import {
  isSamePractitionerReference,
  isTeleconsultoriaParticipant,
  teleconsultoriaGuestOfAppointment,
} from "../../utils/actors";
import { patientParticipantActor } from "../../utils/teleconsultoriaAppointment";
import type { BookedTeleconsultoriaRow } from "../types";

/** Patients incluídos no bundle (`_include=Appointment:patient`), por id. */
export function patientsByIdFrom(resources: Array<fhir5.Resource | undefined>) {
  return new Map(
    resources
      .filter((resource): resource is fhir5.Patient => resource?.resourceType === "Patient")
      .map((patient) => [patient.id || "", patient]),
  );
}

/** Nome do paciente: display do participant → Patient incluído → fallback. */
function patientName(
  appointment: fhir5.Appointment,
  patientsById: Map<string, fhir5.Patient>,
  fallback: string,
) {
  const actor = patientParticipantActor(appointment);
  if (actor?.display) return actor.display;
  const patientId = actor?.reference?.split("/")[1];
  const patient = patientId ? patientsById.get(patientId) : undefined;
  return patient?.name?.[0]?.text || fallback;
}

/**
 * Teleconsultor: dono da agenda (quando informado) ou o convidado do Appointment
 * (CON ou, em teleconsultoria sem marcação, a regra da ordem).
 */
function teleconsultorOf(appointment: fhir5.Appointment, ownerReferences?: Set<string>) {
  const owner = ownerReferences?.size
    ? appointment.participant?.find((item) => ownerReferences.has(item.actor?.reference || ""))
        ?.actor
    : undefined;
  return owner || teleconsultoriaGuestOfAppointment(appointment).practitioner;
}

/** Practitioners que não são o teleconsultor (ex.: solicitante da afiliada). */
function requesterNames(appointment: fhir5.Appointment, teleconsultorReference: string): string[] {
  return (appointment.participant || [])
    .filter(
      (item) =>
        item.actor?.reference?.includes("Practitioner/") &&
        !isSamePractitionerReference(item.actor.reference, teleconsultorReference) &&
        !isTeleconsultoriaParticipant(item),
    )
    .map((item) => item.actor?.display || "")
    .filter(Boolean);
}

/** Junta listas sem repetir o mesmo Appointment; ordena pelo horário. */
export function mergeBookedRows(...sources: BookedTeleconsultoriaRow[][]): BookedTeleconsultoriaRow[] {
  const byId = new Map<string, BookedTeleconsultoriaRow>();
  sources.flat().forEach((row) => {
    if (!byId.has(row.appointmentId)) byId.set(row.appointmentId, row);
  });
  return [...byId.values()].sort(
    (first, second) => new Date(first.start || 0).getTime() - new Date(second.start || 0).getTime(),
  );
}

/** Appointment → linha das listas de teleconsultorias da prestadora. */
export function toBookedRow(
  appointment: fhir5.Appointment,
  options: {
    patientsById?: Map<string, fhir5.Patient>;
    ownerReferences?: Set<string>;
    fallbackPatientName?: string;
  } = {},
): BookedTeleconsultoriaRow | undefined {
  if (!appointment.id) return undefined;
  const teleconsultor = teleconsultorOf(appointment, options.ownerReferences);
  return {
    appointment,
    appointmentId: appointment.id,
    start: appointment.start,
    status: appointment.status,
    patientName: patientName(
      appointment,
      options.patientsById || new Map(),
      options.fallbackPatientName || "Paciente",
    ),
    teleconsultorName: teleconsultor?.display || "Teleconsultor",
    teleconsultorReference: teleconsultor?.reference,
    requesterNames: requesterNames(appointment, teleconsultor?.reference || ""),
  };
}
